/**
 * Methodology Page
 *
 * Explains how the verification tool works.
 * Framed as a source-claim verification tool, not a traditional fact-checker.
 */

export const Methodology = () => {
  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem' }}>
      <h1 style={{ color: '#2c3e50', marginBottom: '1.5rem' }}>How It Works</h1>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#34495e' }}>What This Tool Does</h2>
        <p style={{ lineHeight: 1.7, color: '#555' }}>
          This is a <strong>source-claim verification tool</strong>. You provide claims and source
          materials, and we analyse whether the sources support those claims.
        </p>
        <p style={{ lineHeight: 1.7, color: '#555', marginTop: '1rem' }}>Common use cases:</p>
        <ul style={{ lineHeight: 1.8, color: '#555', paddingLeft: '1.5rem' }}>
          <li>Checking AI-generated content for citation accuracy</li>
          <li>Verifying that sources support claims in documents</li>
          <li>Quality assurance for research and writing</li>
        </ul>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#34495e' }}>What This Tool Does Not Do</h2>
        <p style={{ lineHeight: 1.7, color: '#555' }}>
          We do not determine whether claims are objectively &quot;true&quot; or &quot;false&quot;
          in the world. We only assess whether your provided sources contain evidence that supports
          or contradicts your provided claims.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#34495e' }}>The Verification Process</h2>
        <ol style={{ lineHeight: 1.8, color: '#555', paddingLeft: '1.5rem' }}>
          <li>
            <strong>You submit claims and sources</strong> - either URLs or uploaded documents
          </li>
          <li>
            <strong>We fetch and extract content</strong> - we retrieve the source material and
            extract readable text
          </li>
          <li>
            <strong>AI analyses the alignment</strong> - our AI system checks whether the source
            contains evidence supporting or contradicting the claim
          </li>
          <li>
            <strong>Results are presented</strong> - with the AI&apos;s assessment, confidence
            level, and relevant quotes from the source
          </li>
          <li>
            <strong>You can review and validate</strong> - use the human validation feature to
            confirm or override the AI&apos;s assessment
          </li>
        </ol>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#34495e' }}>Assessment Categories</h2>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '1rem',
          }}
        >
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th
                style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                }}
              >
                Result
              </th>
              <th
                style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                }}
              >
                Meaning
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                style={{
                  padding: '0.75rem',
                  borderBottom: '1px solid #dee2e6',
                  fontWeight: 600,
                  color: '#27ae60',
                }}
              >
                SUPPORTS
              </td>
              <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                The source contains evidence that supports the claim
              </td>
            </tr>
            <tr>
              <td
                style={{
                  padding: '0.75rem',
                  borderBottom: '1px solid #dee2e6',
                  fontWeight: 600,
                  color: '#e74c3c',
                }}
              >
                REFUTES
              </td>
              <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                The source contains evidence that contradicts the claim
              </td>
            </tr>
            <tr>
              <td
                style={{
                  padding: '0.75rem',
                  borderBottom: '1px solid #dee2e6',
                  fontWeight: 600,
                  color: '#f39c12',
                }}
              >
                NOT ENOUGH INFO
              </td>
              <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                The source doesn&apos;t contain enough information to assess the claim
              </td>
            </tr>
          </tbody>
        </table>
        <p style={{ lineHeight: 1.7, color: '#555', marginTop: '1rem' }}>
          Each result also includes a confidence level (high, medium, low) based on how clearly the
          source addresses the claim.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#34495e' }}>AI and Its Limitations</h2>
        <p style={{ lineHeight: 1.7, color: '#555' }}>
          We use large language models (LLMs) to analyse source-claim alignment. These systems are
          powerful but imperfect:
        </p>
        <ul style={{ lineHeight: 1.8, color: '#555', paddingLeft: '1.5rem' }}>
          <li>They may misinterpret nuanced or technical content</li>
          <li>They can miss context that a human reader would catch</li>
          <li>They sometimes produce confident-sounding but incorrect assessments</li>
          <li>Paywalled or inaccessible content limits verification accuracy</li>
        </ul>
        <p style={{ lineHeight: 1.7, color: '#555', marginTop: '1rem' }}>
          This is why we provide the <strong>human validation feature</strong> - so you can review
          and correct AI assessments based on your own judgement.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#34495e' }}>Source Credibility</h2>
        <p style={{ lineHeight: 1.7, color: '#555' }}>
          Where possible, we assess source characteristics such as:
        </p>
        <ul style={{ lineHeight: 1.8, color: '#555', paddingLeft: '1.5rem' }}>
          <li>Source type (academic, government, news, blog, etc.)</li>
          <li>Whether it&apos;s a primary or secondary source</li>
          <li>Content completeness (paywalls, abstracts only, etc.)</li>
        </ul>
        <p style={{ lineHeight: 1.7, color: '#555', marginTop: '1rem' }}>
          These assessments help you evaluate the quality of the evidence, but you should apply your
          own judgement about source reliability.
        </p>
      </section>

      <section>
        <h2 style={{ color: '#34495e' }}>Questions?</h2>
        <p style={{ lineHeight: 1.7, color: '#555' }}>
          If you have questions about how the tool works, please get in touch via our{' '}
          <a href="/feedback" style={{ color: '#3498db' }}>
            feedback page
          </a>
          .
        </p>
      </section>

      <footer style={{ marginTop: '3rem', paddingTop: '1rem', borderTop: '1px solid #dee2e6' }}>
        <p style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>Last updated: February 2026</p>
      </footer>
    </div>
  )
}
